import { tz } from "@date-fns/tz";
import CalendarToday from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, InputAdornment, type TextFieldProps } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker, type DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { frFR } from "@mui/x-date-pickers/locales/frFR";
import type { DateValidationError, PickerChangeHandlerContext } from "@mui/x-date-pickers/models";
import { isValidDate } from "@root/utils/date/isValidDate";
import { startOfDay } from "date-fns";
import { fr } from "date-fns/locale/fr";
import { useCallback, useMemo } from "react";

export interface IDateInputProps extends Omit<DatePickerProps<Date>, "renderInput"> {
  TextFieldProps?: TextFieldProps;
  onClear?: () => void;
  timeZone: string;
}

export const PAST_DATES_MIN_DATE = new Date(1900, 0, 1);

const DEFAULT_MAX_DATE = new Date(2100, 11, 31);

// AdapterDateFns has no timezone support — formats/creates dates in browser local timezone.
// We bridge this by:
//  - displayValue: extract the calendar day in org-tz, re-create as midnight browser-local → picker shows and highlights the correct day
//  - handleChange: convert midnight browser-local from picker back to midnight org-tz
export const DateInputV2 = ({
  TextFieldProps,
  onClear,
  disabled,
  onChange,
  value,
  timeZone,
  minDate = PAST_DATES_MIN_DATE,
  maxDate = DEFAULT_MAX_DATE,
  ...props
}: IDateInputProps) => {
  const displayValue = useMemo(() => {
    if (!value || !(value instanceof Date) || Number.isNaN(value.getTime())) {
      return value ?? null;
    }
    const [year, month, day] = new Intl.DateTimeFormat("en-CA", { timeZone }).format(value).split("-").map(Number);
    return new Date(year, (month as number) - 1, day);
  }, [value, timeZone]);

  const handleChange = useCallback(
    (date: Date | null, ctx: PickerChangeHandlerContext<DateValidationError>) => {
      if (!onChange) {
        return;
      }
      if (!isValidDate(date)) {
        onChange(null, ctx);
        return;
      }
      if (ctx.validationError === "minDate" || ctx.validationError === "maxDate") {
        return;
      }
      onChange(new Date(startOfDay(date, { in: tz(timeZone) }).getTime()), ctx);
    },
    [onChange, timeZone]
  );

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={fr}
      localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <DatePicker
        {...props}
        minDate={minDate}
        maxDate={maxDate}
        value={displayValue}
        onChange={handleChange}
        slotProps={{
          ...props.slotProps,
          textField: {
            ...props.slotProps?.textField,
            ...TextFieldProps,
            ...(!disabled && onClear
              ? {
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => onClear()}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                    ...TextFieldProps?.InputProps,
                  },
                }
              : {}),
          },
          openPickerIcon: { color: "secondary" },
          openPickerButton: { size: "small" },
          inputAdornment: { position: "start" },
        }}
        slots={{ openPickerIcon: CalendarToday }}
        disabled={disabled}
      />
    </LocalizationProvider>
  );
};
